const LAYOUT_ROUNDS = {
  BRACKET_32_TO_8: [{ code: 'R32', count: 16 }, { code: 'R16', count: 8 }],
  BRACKET_8_TO_CHAMPION: [{ code: 'QF', count: 4 }, { code: 'SF', count: 2 }, { code: 'F', count: 1 }]
};

const MATCH_STATUSES = ['PENDING', 'READY', 'FINISHED'];
const SOURCE_TYPES = ['TEAM', 'WINNER', 'TBD'];
const WINNER_CAMPS = ['SONG', 'JIN'];

export const DEFAULT_RUNTIME = {
  pollingEnabled: true,
  refreshIntervalMs: 60000,
  requestTimeoutMs: 10000
};

const isObject = value => Object.prototype.toString.call(value) === '[object Object]';
const hasOwn = (target, key) => Object.prototype.hasOwnProperty.call(target, key);

const invalidConfig = message => {
  const error = new Error(`对阵配置校验失败：${message}`);
  error.code = 'INVALID_MATCH_CONFIG';
  return error;
};

const assertConfig = (condition, message) => {
  if (!condition) {
    throw invalidConfig(message);
  }
};

const assertNonEmptyString = (value, label) => {
  assertConfig(typeof value === 'string' && value.trim(), `${label} 必须是非空字符串`);
};

const createIndex = (items, label) => {
  assertConfig(Array.isArray(items), `${label} 必须是数组`);
  const index = Object.create(null);

  items.forEach(item => {
    assertConfig(isObject(item), `${label}存在非对象项`);
    assertNonEmptyString(item.id, `${label} id`);
    assertConfig(!hasOwn(index, item.id), `${label}存在重复 id：${item.id}`);
    index[item.id] = item;
  });

  return index;
};

const sortByOrder = (left, right) => Number(left.order) - Number(right.order);

const getSlot = (match, slotNumber) => match.slots.find(slot => slot.slot === slotNumber);

const normalizeRuntime = runtime => {
  const source = isObject(runtime) ? runtime : {};
  const refreshIntervalMs = Number(source.refreshIntervalMs);
  const requestTimeoutMs = Number(source.requestTimeoutMs);

  return {
    pollingEnabled: source.pollingEnabled !== false,
    refreshIntervalMs: isFinite(refreshIntervalMs) && refreshIntervalMs >= 10000 ? refreshIntervalMs : DEFAULT_RUNTIME.refreshIntervalMs,
    requestTimeoutMs: isFinite(requestTimeoutMs) && requestTimeoutMs > 0 ? requestTimeoutMs : DEFAULT_RUNTIME.requestTimeoutMs
  };
};

const createRoundIndex = stages => {
  const roundIndex = Object.create(null);

  stages.forEach(stage => {
    assertConfig(Array.isArray(stage.rounds) && stage.rounds.length > 0, `赛段 ${stage.id} 缺少 rounds`);
    const stageRounds = Object.create(null);
    stage.rounds.forEach(round => {
      assertConfig(isObject(round), `赛段 ${stage.id} 存在无效轮次`);
      assertNonEmptyString(round.code, `赛段 ${stage.id} 轮次 code`);
      assertConfig(!hasOwn(stageRounds, round.code), `赛段 ${stage.id} 存在重复轮次：${round.code}`);
      assertConfig(isFinite(Number(round.order)), `赛段 ${stage.id} 轮次 ${round.code} 缺少有效 order`);
      stageRounds[round.code] = round;
    });
    roundIndex[stage.id] = stageRounds;
  });

  return roundIndex;
};

const createMatchesByRound = matches => {
  const result = Object.create(null);

  matches.forEach(match => {
    const key = `${match.stageId}:${match.roundCode}`;
    if (!result[key]) {
      result[key] = [];
    }
    result[key].push(match);
  });

  Object.keys(result).forEach(key => {
    const orders = Object.create(null);
    result[key].forEach(match => {
      const order = Number(match.order);
      assertConfig(isFinite(order), `比赛 ${match.id} 缺少有效 order`);
      assertConfig(!hasOwn(orders, order), `轮次 ${key} 存在重复 order：${order}`);
      orders[order] = true;
    });
    result[key].sort(sortByOrder);
  });

  return result;
};

const validateOrderedWinnerSources = (currentMatches, previousMatches, currentCode, previousCode) => {
  assertConfig(previousMatches.length === currentMatches.length * 2, `轮次 ${currentCode} 与 ${previousCode} 的场次数量无法形成有序分支`);

  currentMatches.forEach((match, matchIndex) => {
    [1, 2].forEach(slotNumber => {
      const source = getSlot(match, slotNumber).source;
      const expectedUpstream = previousMatches[matchIndex * 2 + slotNumber - 1];
      assertConfig(source.type === 'WINNER', `比赛 ${match.id} 的 slot ${slotNumber} 必须引用 ${previousCode} 的胜者`);
      assertConfig(
        source.refId === expectedUpstream.id,
        `比赛 ${match.id} 的 slot ${slotNumber} 必须按顺序引用 ${expectedUpstream.id}，实际为 ${source.refId}`
      );
    });
  });
};

const validateWinnerSources = (currentMatches, previousMatches, currentCode, previousCode) => {
  assertConfig(previousMatches.length === currentMatches.length * 2, `轮次 ${currentCode} 与 ${previousCode} 的场次数量无法形成晋级分支`);

  const previousMatchIndex = createIndex(previousMatches, `轮次 ${previousCode} 的比赛`);
  const referencedMatches = Object.create(null);

  currentMatches.forEach(match => {
    match.slots.forEach(slot => {
      const source = slot.source;
      assertConfig(source.type === 'WINNER', `比赛 ${match.id} 的 slot ${slot.slot} 必须引用 ${previousCode} 的胜者`);
      assertConfig(hasOwn(previousMatchIndex, source.refId), `比赛 ${match.id} 的 slot ${slot.slot} 必须引用 ${previousCode} 的比赛`);
      assertConfig(!referencedMatches[source.refId], `比赛 ${source.refId} 不能重复晋级到轮次 ${currentCode}`);
      referencedMatches[source.refId] = true;
    });
  });

  previousMatches.forEach(match => {
    assertConfig(referencedMatches[match.id], `比赛 ${match.id} 缺少进入轮次 ${currentCode} 的胜者来源`);
  });
};

const validateInternalRoundChain = (stage, expectedRounds, matchesByRound) => {
  for (let index = 1; index < expectedRounds.length; index++) {
    const previousCode = expectedRounds[index - 1].code;
    const currentCode = expectedRounds[index].code;
    const previousMatches = matchesByRound[`${stage.id}:${previousCode}`] || [];
    const currentMatches = matchesByRound[`${stage.id}:${currentCode}`] || [];
    const validateSources = stage.layoutType === 'BRACKET_32_TO_8' ? validateOrderedWinnerSources : validateWinnerSources;
    validateSources(currentMatches, previousMatches, currentCode, previousCode);
  }
};

const validateCrossStageEntry = (stages, matchesByRound) => {
  const sourceStages = stages.filter(stage => stage.layoutType === 'BRACKET_32_TO_8');
  const targetStages = stages.filter(stage => stage.layoutType === 'BRACKET_8_TO_CHAMPION');
  assertConfig(sourceStages.length === 1, '配置必须且只能包含一个 BRACKET_32_TO_8 赛段');
  assertConfig(targetStages.length === 1, '配置必须且只能包含一个 BRACKET_8_TO_CHAMPION 赛段');

  const sourceStage = sourceStages[0];
  const targetStage = targetStages[0];
  const previousMatches = matchesByRound[`${sourceStage.id}:R16`] || [];
  const currentMatches = matchesByRound[`${targetStage.id}:QF`] || [];
  validateWinnerSources(currentMatches, previousMatches, `${targetStage.id}/QF`, `${sourceStage.id}/R16`);
};

const validateLayouts = (stages, matchesByRound) => {
  stages.forEach(stage => {
    const expectedRounds = LAYOUT_ROUNDS[stage.layoutType];
    assertConfig(expectedRounds, `赛段 ${stage.id} 使用了未知 layoutType：${stage.layoutType}`);
    const configuredCodes = stage.rounds.map(round => round.code);
    assertConfig(configuredCodes.length === expectedRounds.length, `赛段 ${stage.id} 的轮次数量与 ${stage.layoutType} 不符`);

    expectedRounds.forEach(expected => {
      assertConfig(configuredCodes.indexOf(expected.code) > -1, `赛段 ${stage.id} 缺少轮次 ${expected.code}`);
      const matches = matchesByRound[`${stage.id}:${expected.code}`] || [];
      assertConfig(matches.length === expected.count, `赛段 ${stage.id} 的 ${expected.code} 必须配置 ${expected.count} 场比赛`);
    });

    validateInternalRoundChain(stage, expectedRounds, matchesByRound);
  });
  validateCrossStageEntry(stages, matchesByRound);
};
const validateCycles = (matches, matchIndex) => {
  const visiting = Object.create(null);
  const visited = Object.create(null);

  const visit = matchId => {
    if (visiting[matchId]) {
      throw invalidConfig(`检测到比赛循环引用：${matchId}`);
    }
    if (visited[matchId]) {
      return;
    }

    visiting[matchId] = true;
    matchIndex[matchId].slots.forEach(slot => {
      if (slot.source.type === 'WINNER') {
        visit(slot.source.refId);
      }
    });
    delete visiting[matchId];
    visited[matchId] = true;
  };

  matches.forEach(match => visit(match.id));
};

const validateConfig = config => {
  assertConfig(isObject(config), 'JSON 根节点必须是对象');
  assertNonEmptyString(config.schemaVersion, 'schemaVersion');
  assertNonEmptyString(config.dataVersion, 'dataVersion');

  const teams = config.teams;
  const stages = config.stages;
  const matches = config.matches;
  const teamIndex = createIndex(teams, '队伍配置');
  const stageIndex = createIndex(stages, '赛段配置');
  const matchIndex = createIndex(matches, '比赛配置');
  const roundIndex = createRoundIndex(stages);

  teams.forEach(team => {
    assertNonEmptyString(team.name, `队伍 ${team.id} 的 name`);
    assertConfig(typeof team.shortName === 'undefined' || typeof team.shortName === 'string', `队伍 ${team.id} 的 shortName 必须是字符串`);
  });

  const stageOrders = Object.create(null);
  stages.forEach(stage => {
    const order = Number(stage.order);
    assertNonEmptyString(stage.title, `赛段 ${stage.id} 的 title`);
    assertConfig(isFinite(order), `赛段 ${stage.id} 缺少有效 order`);
    assertConfig(!hasOwn(stageOrders, order), `赛段配置存在重复 order：${order}`);
    assertConfig(typeof stage.visible === 'undefined' || typeof stage.visible === 'boolean', `赛段 ${stage.id} 的 visible 必须是布尔值`);
    stageOrders[order] = true;
  });

  matches.forEach(match => {
    assertConfig(hasOwn(stageIndex, match.stageId), `比赛 ${match.id} 引用了不存在的赛段：${match.stageId}`);
    assertConfig(hasOwn(roundIndex[match.stageId], match.roundCode), `比赛 ${match.id} 引用了不存在的轮次：${match.roundCode}`);
    assertConfig(Array.isArray(match.slots) && match.slots.length === 2, `比赛 ${match.id} 必须且只能配置两个参赛位`);

    const seenSlots = Object.create(null);
    match.slots.forEach(slot => {
      assertConfig(isObject(slot) && (slot.slot === 1 || slot.slot === 2), `比赛 ${match.id} 的 slot 只能是 1 或 2`);
      assertConfig(!seenSlots[slot.slot], `比赛 ${match.id} 存在重复 slot：${slot.slot}`);
      seenSlots[slot.slot] = true;
      assertConfig(isObject(slot.source) && SOURCE_TYPES.indexOf(slot.source.type) > -1, `比赛 ${match.id} 使用了不支持的 source.type`);
      assertConfig(typeof slot.seedCode === 'undefined' || typeof slot.seedCode === 'string', `比赛 ${match.id} 的 slot ${slot.slot} 的 seedCode 必须是字符串`);

      if (slot.source.type === 'TEAM') {
        assertConfig(hasOwn(teamIndex, slot.source.refId), `比赛 ${match.id} 引用了不存在的队伍：${slot.source.refId}`);
      }
      if (slot.source.type === 'WINNER') {
        assertConfig(hasOwn(matchIndex, slot.source.refId), `比赛 ${match.id} 引用了不存在的上游比赛：${slot.source.refId}`);
        assertConfig(slot.source.refId !== match.id, `比赛 ${match.id} 不能引用自己的胜者`);
      }
    });

    assertConfig(MATCH_STATUSES.indexOf(match.status) > -1, `比赛 ${match.id} 使用了不支持的 status：${match.status}`);
    if (match.status === 'FINISHED') {
      assertConfig(match.winnerSlot === 1 || match.winnerSlot === 2, `已结束比赛 ${match.id} 必须设置 winnerSlot`);
    } else {
      assertConfig(match.winnerSlot === null, `未结束比赛 ${match.id} 的 winnerSlot 必须为 null`);
    }
    assertConfig(match.winnerCamp === null || WINNER_CAMPS.indexOf(match.winnerCamp) > -1, `比赛 ${match.id} 的 winnerCamp 只能是 SONG、JIN 或 null`);
  });

  validateCycles(matches, matchIndex);
  const matchesByRound = createMatchesByRound(matches);
  validateLayouts(stages, matchesByRound);

  const winnerCache = Object.create(null);
  const resolveSource = source => {
    if (!source || source.type === 'TBD') {
      return null;
    }
    if (source.type === 'TEAM') {
      return teamIndex[source.refId];
    }
    return resolveWinner(matchIndex[source.refId]);
  };
  const resolveWinner = match => {
    if (!match || match.status !== 'FINISHED') {
      return null;
    }
    if (hasOwn(winnerCache, match.id)) {
      return winnerCache[match.id];
    }
    const team = resolveSource(getSlot(match, match.winnerSlot).source);
    assertConfig(team, `已结束比赛 ${match.id} 的胜者无法解析`);
    winnerCache[match.id] = team;
    return team;
  };

  matches.forEach(match => {
    if (match.status === 'FINISHED') {
      resolveWinner(match);
    }
    if (match.status === 'READY') {
      assertConfig(
        resolveSource(getSlot(match, 1).source) && resolveSource(getSlot(match, 2).source),
        `待比赛节点 ${match.id} 的双方队伍尚未完整确定，应改为 PENDING`
      );
    }
  });

  return { teams, stages, matches, teamIndex, stageIndex, matchIndex, matchesByRound, resolveSource, resolveWinner };
};

const createParticipant = (slot, match, context, emptyText, fallbackId) => {
  const source = slot && slot.source;
  const team = context.resolveSource(source);
  const isWinner = Boolean(match && match.status === 'FINISHED' && match.winnerSlot === slot.slot);
  const placeholder = slot && typeof slot.seedCode === 'string' && slot.seedCode.trim() ? slot.seedCode.trim() : emptyText;

  return {
    id: match ? `${match.id}-slot-${slot.slot}` : fallbackId,
    name: team ? team.shortName || team.name : placeholder,
    fullName: team ? team.name : placeholder,
    isWinner,
    camp: isWinner ? match.winnerCamp : null
  };
};

const createWinnerSlot = match => ({
  slot: match.winnerSlot,
  source: { type: 'WINNER', refId: match.id }
});

const getWinnerSourceMatches = (match, matchIndex) => match.slots.map(slot => matchIndex[slot.source.refId]).sort(sortByOrder);

const getWinnerSourceSlot = (match, sourceMatchId) => match.slots.find(slot => slot.source.type === 'WINNER' && slot.source.refId === sourceMatchId);

const createSide = (firstMatches, nextMatch, finalSlot, finalMatch, context, emptyText, sideId) => {
  const firstRound = [];
  firstMatches.forEach(match => {
    firstRound.push(createParticipant(getSlot(match, 1), match, context, emptyText));
    firstRound.push(createParticipant(getSlot(match, 2), match, context, emptyText));
  });

  return {
    id: sideId,
    firstRound,
    secondRound: [
      createParticipant(getSlot(nextMatch, 1), nextMatch, context, emptyText),
      createParticipant(getSlot(nextMatch, 2), nextMatch, context, emptyText)
    ],
    finalist: createParticipant(finalSlot, finalMatch, context, emptyText, `${sideId}-finalist`)
  };
};

const buildStage32 = (stage, context, emptyText) => {
  const firstRound = context.matchesByRound[`${stage.id}:R32`];
  const secondRound = context.matchesByRound[`${stage.id}:R16`];
  const groups = [];

  for (let block = 0; block < 4; block++) {
    const leftNext = secondRound[block * 2];
    const rightNext = secondRound[block * 2 + 1];
    groups.push({
      id: `${stage.id}-group-${block + 1}`,
      left: createSide(
        [firstRound[block * 4], firstRound[block * 4 + 1]],
        leftNext,
        createWinnerSlot(leftNext),
        leftNext,
        context,
        emptyText,
        `${stage.id}-${block}-left`
      ),
      right: createSide(
        [firstRound[block * 4 + 2], firstRound[block * 4 + 3]],
        rightNext,
        createWinnerSlot(rightNext),
        rightNext,
        context,
        emptyText,
        `${stage.id}-${block}-right`
      )
    });
  }

  return groups;
};

const buildStage8 = (stage, context, emptyText) => {
  const semiFinals = context.matchesByRound[`${stage.id}:SF`];
  const finalMatch = context.matchesByRound[`${stage.id}:F`][0];
  const champion = context.resolveWinner(finalMatch);
  const leftSemiFinal = semiFinals[0];
  const rightSemiFinal = semiFinals[1];

  return {
    champion: champion ? champion.shortName || champion.name : emptyText,
    championFullName: champion ? champion.name : emptyText,
    groups: [
      {
        id: `${stage.id}-final-group`,
        left: createSide(
          getWinnerSourceMatches(leftSemiFinal, context.matchIndex),
          leftSemiFinal,
          getWinnerSourceSlot(finalMatch, leftSemiFinal.id),
          finalMatch,
          context,
          emptyText,
          `${stage.id}-left`
        ),
        right: createSide(
          getWinnerSourceMatches(rightSemiFinal, context.matchIndex),
          rightSemiFinal,
          getWinnerSourceSlot(finalMatch, rightSemiFinal.id),
          finalMatch,
          context,
          emptyText,
          `${stage.id}-right`
        )
      }
    ]
  };
};

export const createMatchConfigViewModel = config => {
  const context = validateConfig(config);
  const emptyText = isObject(config.ui) && typeof config.ui.emptyText === 'string' && config.ui.emptyText.trim() ? config.ui.emptyText : '虚位以待';
  const visibleStages = context.stages
    .filter(stage => stage.visible !== false)
    .slice()
    .sort(sortByOrder);
  assertConfig(visibleStages.length > 0, '没有可展示的赛段');

  const stages = visibleStages.map(stage => {
    const viewModel = {
      id: stage.id,
      title: stage.title,
      order: Number(stage.order),
      layoutType: stage.layoutType
    };

    if (stage.layoutType === 'BRACKET_32_TO_8') {
      viewModel.groups = buildStage32(stage, context, emptyText);
    } else {
      const result = buildStage8(stage, context, emptyText);
      viewModel.groups = result.groups;
      viewModel.champion = result.champion;
      viewModel.championFullName = result.championFullName;
    }
    return viewModel;
  });

  const configuredDefault = stages.find(stage => stage.id === config.defaultStageId);
  return {
    schemaVersion: config.schemaVersion,
    dataVersion: config.dataVersion,
    updatedAt: config.updatedAt || '',
    emptyText,
    loadErrorText: isObject(config.ui) && config.ui.loadErrorText ? config.ui.loadErrorText : '对阵信息加载失败，请稍后刷新',
    defaultStageId: configuredDefault ? configuredDefault.id : stages[0].id,
    runtime: normalizeRuntime(config.runtime),
    stages
  };
};
